import { SegmentedControl as PackageSegmentedControl } from '@pms-digital/design-system';

/**
 * Une bascule segmentée JOINTE : n segments under one border (maquettes
 * `Timeline.dc.html`, `BarreDeTheme.dc.html` ; issue #57).
 *
 * The mockup drew two variants and left the choice to the code (« la variante
 * de bascule segmentée (jointe ou séparée) à retenir »): the JOINT one is
 * retained, and it is now the `SegmentedControl` of the design system, which
 * draws exactly that - a 36 px frame of joined cells, the chosen one in bleu
 * nuit.
 *
 * ## Buttons with `aria-pressed`, not radios
 *
 * The persona's involvement scale is a `radiogroup` of real radios, and its own
 * comment says why: a scale of named crans wants arrow keys and a reported
 * state. THIS control is a pair or a triple of toggles sitting in a dense panel
 * of twenty of them - a named group plus `aria-pressed` is what the package
 * draws, it keeps every segment in the tab order, and it does not claim a
 * roving focus the panel never implements. One register, one behaviour.
 *
 * What stays here is the typing and two things the package does not take yet:
 * generic over the value so a caller never casts (the three speeds, the three
 * shapes and the two formats are all closed unions, and a control typed on
 * `string` would let a typo through to `onChange`), the `data-testid` of the
 * group, and `disabled` - the package control has no disabled state, so a
 * read-only panel makes the envelope `inert` and faint instead.
 */

export interface SegmentedOption<Value extends string> {
  readonly value: Value;
  readonly label: string;
}

export interface SegmentedControlProps<Value extends string> {
  /** The group's accessible name - « Format de l'aperçu », « Rapidité ». */
  readonly label: string;
  readonly options: readonly SegmentedOption<Value>[];
  readonly value: Value;
  readonly disabled?: boolean;
  readonly testId?: string;
  readonly onChange: (next: Value) => void;
}

export function SegmentedControl<Value extends string>({
  label,
  options,
  value,
  disabled = false,
  testId,
  onChange,
}: SegmentedControlProps<Value>) {
  return (
    <div
      {...(testId === undefined ? {} : { 'data-testid': testId })}
      inert={disabled}
      className={`inline-flex max-w-full ${disabled ? 'opacity-60' : ''}`}
    >
      <PackageSegmentedControl
        label={label}
        options={options}
        value={value}
        onChange={(next) => {
          // The package hands back a `string`; it can only be one of OUR values.
          const chosen = options.find((option) => option.value === next);
          if (chosen !== undefined) onChange(chosen.value);
        }}
      />
    </div>
  );
}
